import { createElement } from 'react';
import { formatHoursMinutes } from '@/shared/utils/metricsFormatters';
import {
  isMetricThresholdDisabled,
  metricThresholdsSuites,
  type MetricThreshold,
} from '@/shared/utils/metricsThresholds';
import { typographyTokens } from '@/theme/tokens';
import { Box, Tooltip } from '@/ui/primitives';
import {
  getStatusToneTextColor,
  normaliseSummaryChipStatus,
  type StatusChipStatus,
} from '@/ui/primitives';
import { formatDuration } from '../../formatters/formatDuration';
import { formatFrequency } from '../../formatters/formatFrequency';
import { formatPercent } from '../../formatters/formatPercent';
import { getDeltaTextColor } from './deltaColors';
import { MetricChipCell } from './tableCells';
import type { TestSummaryItem } from './tableTypes';
import type { BoxProps } from '@/ui/primitives';
import type {
  ReleaseFeatureSummaryDto,
  ReleaseSuiteSummaryDto,
  ReleaseTestSummaryDto,
  SuiteExecutionSummaryDto,
  TestExecutionSummaryDto,
} from '@flowqa/shared';
import type { Theme } from '@mui/material/styles';
import type {
  GridAlignment,
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
} from '@mui/x-data-grid';
import type { ReactNode } from 'react';

export type MetricThresholds = MetricThreshold;
type ThresholdChipKey = 'green' | 'yellow' | 'red';

export const getThresholdChipKey = (
  value: number | null | undefined,
  thresholds: MetricThresholds,
): ThresholdChipKey | null => {
  if (value === null || value === undefined) return null;
  if (isMetricThresholdDisabled(thresholds)) return null;
  if (thresholds.direction === 'higherBetter') {
    if (value >= thresholds.target) return 'green';
    if (value >= thresholds.warn) return 'yellow';
    return 'red';
  }
  if (value <= thresholds.target) return 'green';
  if (value <= thresholds.warn) return 'yellow';
  return 'red';
};

export type MetricColumnKey = 'passRate' | 'frequency' | 'avgDuration' | 'flakeScore' | 'mttf';

export const metricColumnOrder: MetricColumnKey[] = [
  'passRate',
  'frequency',
  'avgDuration',
  'flakeScore',
  'mttf',
];

export type MetricColumnFields = {
  passRate: string;
  frequency: string;
  avgDuration: string;
  flakeScore: string;
  mttf: string;
};

export type TableMetricColumnsConfig<T extends GridValidRowModel> = {
  fields: Partial<MetricColumnFields>;
  flex?: Partial<Record<MetricColumnKey, number>>;
  deltaFlex?: Partial<Record<MetricColumnKey, number>>;
  deltaFields?: Partial<Record<MetricColumnKey, string>>;
  headers?: Partial<Record<MetricColumnKey, string>>;
  thresholds?: Partial<Record<MetricColumnKey, MetricThresholds>>;
  resolveThresholds?: (row: T) => Partial<Record<MetricColumnKey, MetricThresholds>> | undefined;
  enabledKeys?: MetricColumnKey[];
  includeDeltas?: boolean | ((key: MetricColumnKey) => boolean);
  formatters?: Partial<Record<MetricColumnKey, (value: number) => string>>;
  deltaFormatters?: Partial<Record<MetricColumnKey, (value: number) => string>>;
  thresholdValueTransforms?: Partial<Record<MetricColumnKey, (value: number) => number>>;
  deltaRenderers?: Partial<
    Record<MetricColumnKey, (value: number | null | undefined) => ReactNode>
  >;
  align?: GridAlignment;
  headerAlign?: GridAlignment;
};

const DEFAULT_METRIC_HEADERS: Record<MetricColumnKey, string> = {
  passRate: 'Pass Rate',
  frequency: 'Frequency',
  avgDuration: 'Avg. Duration',
  flakeScore: 'Flake Score',
  mttf: 'Mean Time To Fix',
};

const DEFAULT_METRIC_FLEX: Record<MetricColumnKey, number> = {
  passRate: 1,
  frequency: 1,
  avgDuration: 1,
  flakeScore: 1,
  mttf: 1,
};

const DEFAULT_METRIC_DELTA_FLEX: Record<MetricColumnKey, number> = {
  passRate: 0.8,
  frequency: 0.8,
  avgDuration: 0.8,
  flakeScore: 0.8,
  mttf: 0.8,
};

const getNumberFieldValueByKey = (
  row: GridValidRowModel | null | undefined,
  field: string,
): number | null | undefined => {
  if (!row) return null;
  const value = field
    .split('.')
    .reduce<unknown>(
      (acc, key) =>
        acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined,
      row,
    );
  if (typeof value === 'number') return value;
  if (value === null) return null;
  if (value === undefined) return undefined;
  return undefined;
};

const isPromiseLike = <T>(value: unknown): value is PromiseLike<T> =>
  typeof value === 'object' &&
  value !== null &&
  'then' in value &&
  typeof (value as { then?: unknown }).then === 'function';

const METRIC_ALIGNMENT: Pick<GridColDef, 'align' | 'headerAlign'> = {
  align: 'center',
  headerAlign: 'center',
};

const METRIC_TEXT_KEYS: MetricColumnKey[] = ['passRate', 'frequency', 'avgDuration'];
const NO_DATA_LABEL = 'No data';
const NUMERIC_TEXT_SX = {};
const DELTA_TEXT_SX = {
  display: 'block',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const mapChipKeyToStatusTone = (
  key: ReturnType<typeof getThresholdChipKey>,
): 'neutral' | 'green' | 'red' | 'orange' | 'brown' | 'blue' | 'purple' | 'teal' => {
  if (key === 'yellow') return 'orange';
  if (key === null) return 'neutral';
  return key;
};

export const buildMetricColumns = <T extends GridValidRowModel>(
  options: TableMetricColumnsConfig<T>,
): Array<GridColDef<T>> => {
  const headers = { ...DEFAULT_METRIC_HEADERS, ...options.headers };
  const flex = { ...DEFAULT_METRIC_FLEX, ...options.flex };
  const deltaFlex = { ...DEFAULT_METRIC_DELTA_FLEX, ...options.deltaFlex };
  const enabledKeys = options.enabledKeys ?? metricColumnOrder;
  const includeDeltas = options.includeDeltas ?? true;
  const includeDeltaForKey = (key: MetricColumnKey): boolean =>
    typeof includeDeltas === 'function' ? includeDeltas(key) : includeDeltas;
  const formatters = options.formatters ?? {};
  const deltaFormatters = options.deltaFormatters ?? {};
  const thresholdValueTransforms = options.thresholdValueTransforms ?? {};
  const deltaRenderers = options.deltaRenderers ?? {};
  const { fields } = options;
  const deltaFields = options.deltaFields ?? {};
  const metricAlign = options.align ?? METRIC_ALIGNMENT.align;
  const metricHeaderAlign = options.headerAlign ?? METRIC_ALIGNMENT.headerAlign;
  const resolveThresholds = options.resolveThresholds;
  const thresholds: Record<MetricColumnKey, MetricThresholds> = {
    passRate: metricThresholdsSuites.passRate,
    frequency: metricThresholdsSuites.frequency,
    avgDuration: metricThresholdsSuites.avgDuration,
    flakeScore: metricThresholdsSuites.flakeScore,
    mttf: metricThresholdsSuites.mttf,
    ...options.thresholds,
  };

  type MetricColumnConfig = {
    key: MetricColumnKey;
    field?: string;
    header: string;
    flex: number;
    formatValue: (value: number) => string;
    thresholdValue?: (value: number) => number;
  };

  type MetricDeltaConfig = {
    key: MetricColumnKey;
    field: string;
    header: string;
    description: string;
    flex: number;
    formatValue: (value: number) => string;
    direction: 'higherBetter' | 'lowerBetter';
  };

  const createMetricColumn = (config: MetricColumnConfig & { field: string }): GridColDef<T> => {
    const column: GridColDef<T> = {
      field: config.field,
      headerName: config.header,
      flex: config.flex,
      sortable: true,
      filterable: false,
      valueGetter: (_value, row) => getNumberFieldValueByKey(row, config.field),
    };
    column.renderCell = (params: GridRenderCellParams<T, number | null | undefined>) => {
      const value = getNumberFieldValueByKey(params.row, config.field);
      const resolvedThresholds = resolveThresholds?.(params.row);
      const thresholdConfig = resolvedThresholds?.[config.key] ?? thresholds[config.key];
      const thresholdValue =
        value === null || value === undefined
          ? null
          : config.thresholdValue
            ? config.thresholdValue(value)
            : value;
      const accentColor = getThresholdChipKey(thresholdValue, thresholdConfig);
      const displayValue = value === null || value === undefined ? '-' : config.formatValue(value);
      if (METRIC_TEXT_KEYS.includes(config.key)) {
        const tone = mapChipKeyToStatusTone(accentColor);
        const color =
          value === null || value === undefined
            ? 'text.secondary'
            : accentColor === null
              ? 'text.primary'
              : (muiTheme: Theme): string => getStatusToneTextColor(muiTheme, tone);
        return createElement(
          Box,
          {
            component: 'span',
            sx: {
              color,
              fontSize: (muiTheme: Theme): string | number =>
                muiTheme.typography.body.fontSize ?? typographyTokens.body.fontSize ?? 14,
              fontWeight: typographyTokens.body.fontWeight,
              ...NUMERIC_TEXT_SX,
            },
          } as BoxProps,
          displayValue,
        );
      }
      return createElement(MetricChipCell, { params, accentColor, displayValue });
    };
    Object.assign(column, { align: metricAlign, headerAlign: metricHeaderAlign });
    return column;
  };

  const formatDeltaValue = (
    value: number | null | undefined,
    formatValue: (absoluteValue: number) => string,
    direction: MetricDeltaConfig['direction'],
  ): { label: string; isGood: boolean } | null => {
    if (value === null || value === undefined || value === 0) return null;
    const absolute = Math.abs(value);
    const formatted = formatValue(absolute);
    const sign = value > 0 ? '+' : '-';
    const isPositive = value > 0;
    const isGood = direction === 'higherBetter' ? isPositive : !isPositive;
    return { label: `${sign}${formatted}`, isGood };
  };

  const createMetricDeltaColumn = (config: MetricDeltaConfig): GridColDef<T> => {
    const column: GridColDef<T> = {
      field: config.field,
      headerName: config.header,
      description: config.description,
      flex: config.flex,
      sortable: true,
      filterable: false,
      renderHeader: () =>
        createElement(Tooltip, {
          title: config.description,
          placement: 'top',
          arrow: true,
          slotProps: {
            tooltip: { sx: { fontSize: (theme: Theme) => theme.typography.body2.fontSize } },
          },
          children: createElement(
            Box,
            {
              component: 'span',
              sx: { cursor: 'help' },
            } as BoxProps,
            config.header,
          ),
        }),
      valueGetter: (_value, row) => getNumberFieldValueByKey(row, config.field),
      renderCell: (params) => {
        const value = getNumberFieldValueByKey(params.row, config.field);
        const result = formatDeltaValue(value, config.formatValue, config.direction);
        const fontSize = typographyTokens.body.fontSize;
        const fontWeight = typographyTokens.body.fontWeight;
        if (!result) {
          return createElement(
            Box,
            {
              component: 'span',
              sx: {
                fontSize,
                fontWeight,
                color: 'text.secondary',
                ...NUMERIC_TEXT_SX,
                ...DELTA_TEXT_SX,
              },
            } as BoxProps,
            NO_DATA_LABEL,
          );
        }
        const color = (muiTheme: Theme): string => getDeltaTextColor(muiTheme, result.isGood);
        return createElement(
          Box,
          {
            component: 'span',
            sx: { color, fontSize, fontWeight, ...NUMERIC_TEXT_SX, ...DELTA_TEXT_SX },
            title: result.label,
          } as BoxProps,
          result.label,
        );
      },
    };
    Object.assign(column, { align: metricAlign, headerAlign: metricHeaderAlign });
    return column;
  };

  const metricConfigs: Record<MetricColumnKey, MetricColumnConfig> = {
    passRate: {
      key: 'passRate',
      field: fields.passRate,
      header: headers.passRate,
      flex: flex.passRate,
      formatValue: formatters.passRate ?? ((value) => `${formatPercent(value)}%`),
      thresholdValue: thresholdValueTransforms.passRate,
    },
    frequency: {
      key: 'frequency',
      field: fields.frequency,
      header: headers.frequency,
      flex: flex.frequency,
      formatValue: formatters.frequency ?? ((value) => formatFrequency(value)),
      thresholdValue: thresholdValueTransforms.frequency,
    },
    avgDuration: {
      key: 'avgDuration',
      field: fields.avgDuration,
      header: headers.avgDuration,
      flex: flex.avgDuration,
      formatValue: formatters.avgDuration ?? ((value) => formatDuration(value)),
      thresholdValue: thresholdValueTransforms.avgDuration ?? ((value) => value / 1000),
    },
    flakeScore: {
      key: 'flakeScore',
      field: fields.flakeScore,
      header: headers.flakeScore,
      flex: flex.flakeScore,
      formatValue: formatters.flakeScore ?? ((value) => `${formatPercent(value)}%`),
      thresholdValue: thresholdValueTransforms.flakeScore,
    },
    mttf: {
      key: 'mttf',
      field: fields.mttf,
      header: headers.mttf,
      flex: flex.mttf,
      formatValue: formatters.mttf ?? ((value) => formatHoursMinutes(value / 3600)),
      thresholdValue: thresholdValueTransforms.mttf ?? ((value) => value / 3600),
    },
  };

  const deltaHeader = 'Δ';

  const deltaConfigs: Record<MetricColumnKey, MetricDeltaConfig> = {
    passRate: {
      key: 'passRate',
      field:
        deltaFields.passRate ?? (fields.passRate ? `${fields.passRate}Delta` : 'passRateDelta'),
      header: deltaHeader,
      description: `${headers.passRate} Delta`,
      flex: deltaFlex.passRate,
      formatValue: deltaFormatters.passRate ?? ((value) => `${formatPercent(value)}%`),
      direction: 'higherBetter',
    },
    frequency: {
      key: 'frequency',
      field:
        deltaFields.frequency ?? (fields.frequency ? `${fields.frequency}Delta` : 'frequencyDelta'),
      header: deltaHeader,
      description: `${headers.frequency} Delta`,
      flex: deltaFlex.frequency,
      formatValue: deltaFormatters.frequency ?? ((value) => formatFrequency(value)),
      direction: 'higherBetter',
    },
    avgDuration: {
      key: 'avgDuration',
      field:
        deltaFields.avgDuration ??
        (fields.avgDuration ? `${fields.avgDuration}Delta` : 'avgDurationDelta'),
      header: deltaHeader,
      description: `${headers.avgDuration} Delta`,
      flex: deltaFlex.avgDuration,
      formatValue: deltaFormatters.avgDuration ?? ((value) => formatDuration(value)),
      direction: 'lowerBetter',
    },
    flakeScore: {
      key: 'flakeScore',
      field:
        deltaFields.flakeScore ??
        (fields.flakeScore ? `${fields.flakeScore}Delta` : 'flakeScoreDelta'),
      header: deltaHeader,
      description: `${headers.flakeScore} Delta`,
      flex: deltaFlex.flakeScore,
      formatValue: deltaFormatters.flakeScore ?? ((value) => `${formatPercent(value)}%`),
      direction: 'lowerBetter',
    },
    mttf: {
      key: 'mttf',
      field: deltaFields.mttf ?? (fields.mttf ? `${fields.mttf}Delta` : 'mttfDelta'),
      header: deltaHeader,
      description: `${headers.mttf} Delta`,
      flex: deltaFlex.mttf,
      formatValue: deltaFormatters.mttf ?? ((value) => formatHoursMinutes(value / 3600)),
      direction: 'higherBetter',
    },
  };

  return enabledKeys.flatMap((key) => {
    const metricConfig = metricConfigs[key];
    if (!metricConfig.field) return [];
    const columns: Array<GridColDef<T>> = [
      createMetricColumn({ ...metricConfig, field: metricConfig.field }),
    ];
    if (!includeDeltaForKey(key)) {
      return columns;
    }
    const customDeltaRenderer = deltaRenderers[key];
    if (customDeltaRenderer) {
      const deltaColumn: GridColDef<T> = {
        field: deltaConfigs[key].field,
        headerName: deltaConfigs[key].header,
        description: deltaConfigs[key].description,
        flex: deltaConfigs[key].flex,
        sortable: true,
        filterable: false,
        renderHeader: () =>
          createElement(Tooltip, {
            title: deltaConfigs[key].description,
            placement: 'top',
            children: createElement(
              Box,
              {
                component: 'span',
                sx: { cursor: 'help' },
              } as BoxProps,
              deltaConfigs[key].header,
            ),
          }),
        valueGetter: (_value, row) => getNumberFieldValueByKey(row, deltaConfigs[key].field),
        renderCell: (params) => {
          const rendered = customDeltaRenderer(
            getNumberFieldValueByKey(params.row, deltaConfigs[key].field),
          );
          if (isPromiseLike(rendered)) return NO_DATA_LABEL;
          const content = rendered ?? NO_DATA_LABEL;
          const title =
            typeof content === 'string' || typeof content === 'number'
              ? String(content)
              : undefined;
          return createElement(
            Box,
            {
              component: 'span',
              sx: DELTA_TEXT_SX,
              title,
            } as BoxProps,
            content,
          );
        },
      };
      Object.assign(deltaColumn, { align: metricAlign, headerAlign: metricHeaderAlign });
      columns.push(deltaColumn);
      return columns;
    }
    columns.push(createMetricDeltaColumn(deltaConfigs[key]));
    return columns;
  });
};

export const mapTestExecutionSummaries = (
  summaries?: TestExecutionSummaryDto[] | null,
): TestSummaryItem[] => {
  if (!Array.isArray(summaries)) return [];
  return summaries.map((summary) => {
    const slug = summary.testSlug ?? summary.testId;
    const executionHref =
      summary.runNumber != null ? `/tests/${slug}/runs/${summary.runNumber}` : null;
    return {
      testId: summary.testId,
      testName: summary.testName ?? null,
      testSlug: summary.testSlug ?? null,
      href: `/tests/${slug}`,
      executionHref,
      status: normaliseSummaryChipStatus(summary.status ?? null),
      executionId: summary.executionId ?? null,
      runNumber: summary.runNumber ?? null,
    };
  });
};

export const parseListString = (value: string | null | undefined, separator = ','): string[] => {
  if (!value) return [];
  return value
    .split(separator)
    .map((part) => part.trim())
    .filter(Boolean);
};

export const mapSuiteExecutionSummaries = (
  summaries?: SuiteExecutionSummaryDto[] | null,
): TestSummaryItem[] => {
  if (!Array.isArray(summaries)) return [];
  return summaries.map((summary) => {
    const slug = summary.suiteSlug ?? summary.suiteId;
    const executionHref =
      summary.runNumber != null ? `/suites/${slug}/runs/${summary.runNumber}` : null;
    return {
      testId: summary.suiteId,
      testName: summary.suiteName ?? null,
      testSlug: summary.suiteSlug ?? null,
      href: `/suites/${slug}`,
      executionHref,
      status: normaliseSummaryChipStatus(summary.status ?? null),
      executionId: summary.executionId ?? null,
      runNumber: summary.runNumber ?? null,
    };
  });
};

export const mapReleaseFeatureSummaries = (
  summaries?: ReleaseFeatureSummaryDto[] | null,
): TestSummaryItem[] => {
  if (!Array.isArray(summaries)) return [];
  return summaries.map((summary) => {
    const slug = summary.slug ?? summary.id;
    return {
      testId: summary.id,
      testName: summary.name ?? null,
      testSlug: summary.slug ?? null,
      href: `/features/${slug}`,
      executionHref: null,
      status: summary.status,
      executionId: null,
      runNumber: null,
    };
  });
};

export const mapReleaseSuiteSummaries = (
  summaries?: ReleaseSuiteSummaryDto[] | null,
): TestSummaryItem[] => {
  if (!Array.isArray(summaries)) return [];
  return summaries.map((summary) => {
    const slug = summary.slug ?? summary.id;
    return {
      testId: summary.id,
      testName: summary.name ?? null,
      testSlug: summary.slug ?? null,
      href: `/suites/${slug}`,
      executionHref: null,
      status: null,
      executionId: null,
      runNumber: null,
    };
  });
};

export const mapReleaseTestSummaries = (
  summaries?: ReleaseTestSummaryDto[] | null,
): TestSummaryItem[] => {
  if (!Array.isArray(summaries)) return [];
  return summaries.map((summary) => {
    const slug = summary.slug ?? summary.id;
    return {
      testId: summary.id,
      testName: summary.name ?? null,
      testSlug: summary.slug ?? null,
      href: `/tests/${slug}`,
      executionHref: null,
      status: null,
      executionId: null,
      runNumber: null,
    };
  });
};

export type SummaryTooltipItem = {
  key: string;
  label: string;
  href: string;
  executionHref: string | null;
  runNumber: number | null;
};

const capitalizeLabel = (value: string): string =>
  value.length > 0 ? `${value[0].toUpperCase()}${value.slice(1)}` : value;

const resolveExecutionHref = (
  unitLabel: string,
  slug: string,
  runNumber: number | null,
): string | null => {
  if (runNumber === null) return null;
  if (unitLabel === 'suite') return `/suites/${slug}/runs/${runNumber}`;
  if (unitLabel === 'test') return `/tests/${slug}/runs/${runNumber}`;
  return null;
};

export const getSummaryTooltipItems = (
  items: TestSummaryItem[] | null | undefined,
  options: { unitLabel: string; status?: StatusChipStatus | null },
): SummaryTooltipItem[] => {
  const resolvedItems = Array.isArray(items) ? items : [];
  const filteredItems =
    options.status === undefined
      ? resolvedItems
      : resolvedItems.filter((item) =>
          options.status === null ? !item.status : item.status === options.status,
        );
  return filteredItems.map((item) => {
    const name =
      typeof item.testName === 'string' && item.testName.trim().length > 0
        ? item.testName
        : `Unknown ${capitalizeLabel(options.unitLabel)}`;
    const slug =
      typeof item.testSlug === 'string' && item.testSlug.trim().length > 0
        ? item.testSlug
        : item.testId;
    const href = item.href ?? `/${options.unitLabel}s/${slug}`;
    const executionHref =
      item.executionHref ?? resolveExecutionHref(options.unitLabel, slug, item.runNumber ?? null);
    return {
      key: `${item.testId}-${options.status ?? 'all'}`,
      label: name,
      href,
      executionHref,
      runNumber: item.runNumber ?? null,
    };
  });
};

export const getSummaryTooltipFallbackLabel = (unitLabel: string): string =>
  `${capitalizeLabel(unitLabel)}s`;
